import type { CSSProperties } from "react";

import {
  type REPORT_SECTION_LABEL_TREATMENTS,
  type ReportTextStyle,
  resolveReportTextStyle,
} from "@/components/ui/report/reportPresentation";
import { REPORT_CSS_VAR_PRIMARY } from "@/components/ui/report/reportTheme";
import { cn } from "@/lib/utils";

export type SectionHeaderLabelTreatment =
  (typeof REPORT_SECTION_LABEL_TREATMENTS)[number];

const UNDERLINE_RULE_WIDTH_PX = 2;

export interface SectionHeaderProps {
  title: string;
  /** Accent color (bar fill, or label and rule color); uses theme primary when omitted. */
  backgroundColor?: string | null;
  /** Defaults to "bar". */
  labelTreatment?: SectionHeaderLabelTreatment | null;
  labelStyle?: ReportTextStyle | null;
  className?: string;
}

/** Full-width section label for report body sections. */
export const SectionHeader = ({
  title,
  backgroundColor,
  labelTreatment,
  labelStyle,
  className,
}: SectionHeaderProps) => {
  if (title.trim().length === 0) {
    return null;
  }

  const accentColor =
    backgroundColor != null && backgroundColor !== ""
      ? backgroundColor
      : `var(${REPORT_CSS_VAR_PRIMARY})`;
  const resolvedLabelTreatment = labelTreatment ?? "bar";
  const isBar = resolvedLabelTreatment === "bar";

  const containerStyle: CSSProperties = isBar
    ? { backgroundColor: accentColor }
    : resolvedLabelTreatment === "underline"
      ? {
          borderBottomWidth: UNDERLINE_RULE_WIDTH_PX,
          borderBottomStyle: "solid",
          borderBottomColor: accentColor,
        }
      : {};

  return (
    <div
      className={cn(
        "w-full",
        isBar && "px-3 py-1.5",
        resolvedLabelTreatment === "underline" && "pb-1",
        className,
      )}
      style={containerStyle}
    >
      <h2
        className={cn(
          "m-0 text-sm font-semibold tracking-wide",
          isBar && "text-white",
        )}
        style={{
          ...(!isBar && { color: accentColor }),
          ...resolveReportTextStyle(labelStyle),
        }}
      >
        {title.trim()}
      </h2>
    </div>
  );
};
